// BuyCredits.js
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    SimpleGrid,
    Spinner,
    Text,
    useColorMode,
    useColorModeValue,
    useToast
} from "@chakra-ui/react";
import SidebarWithHeader from "components/SidebarWithHeader";
import {
    useBuyCreditsMutation,
    useGetPlansQuery,
} from "store/Subscription/SubscriptionApi";

function normalizePlans(raw) {
  const list =
    raw ??
    [];

    const normalized = list.map((p, i) => {
        const price = p.price ?? p.amountUsd ?? p.amount ?? 0;
        const originalPrice = p.originalPrice ?? p.compareAtPrice ?? p.mrp ?? p.listPrice ?? null;
        
        let discountLabel = null;
        if (originalPrice && price && originalPrice > price) {
            const discountPercent = Math.round(((originalPrice - price) / originalPrice) * 100);
            discountLabel = `${discountPercent}% off`;
        }
        
        return {
            id: p.id ?? p.priceId ?? p._id ?? String(i),
            name: p.name ?? `${p.credits || p.quantity || p.tokens || 0} Credits`,
            credits: p.credits ?? p.quantity ?? p.tokens ?? p.amount ?? 0,
            price,
            originalPrice,
            discountLabel,
            badge: p.badge ?? (p.isPopular ? "People Choice" : null),
            isPopular: Boolean(p.isPopular),
            note: p.note ?? "Credits never expire. VAT may apply.",
            priceId: p.priceId ?? p.id ?? p._id ?? null,
            button: p.button ?? "Buy now",
        };
    });
      
  // If no originalPrice but you want a strike-through, fabricate from price
  return normalized.map((p) => ({
    ...p,
    originalPrice:
      p.originalPrice && p.originalPrice > p.price ? p.originalPrice : null,
  }));
}

const BuyingCard = ({
  plan,
  colorMode,
  isSelected,
  onBuy,
  isBuying,
  canHover = true,
}) => {
  const whiteColor = useColorModeValue("#1a1a1a", "#ffffff");
  const tileBg = colorMode === "light" ? "white" : "background.dark";

  return (
    <Box
      mt={6}
      key={plan.id}
      pos="relative"
      role="group"
      display="flex"
      flexDir="column"
      sx={{ transform: isSelected ? "translate(-10px, -10px)" : "none" }}
    >
      <Flex
        flexDir="column"
        flexGrow={1}
        py={4}
        px={{ base: 6, md: 8 }}
        position="relative"
        zIndex={10}
        bg={tileBg}
        border="1px solid"
        borderColor="mediumPurple"
        borderRadius="20px"
        transition="all 0.3s ease"
        justifyContent="space-between"
        minH={"360px"}
        rowGap={{ base: 2, md: 0 }}
        {...(canHover && { _groupHover: { transform: "translate(-10px, -10px)" } })}
      >
        <Box>
          {plan.badge && (
            <Badge
              colorScheme="purple"
              position="absolute"
              top={3}
              right={3}
              fontSize="0.7rem"
              px={2}
              py={1}
              rounded="md"
            >
              {plan.badge}
            </Badge>
          )}

          {/* “Radio” pill with credits, like the screenshot */}
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            bg={colorMode === "light" ? "gray.100" : "blackAlpha.400"}
            borderRadius="20px"
            px={3}
            py={1}
            mb={3}
          >
            <Box
              w="14px"
              h="14px"
              borderRadius="50%"
              border="2px solid"
              borderColor="gray.400"
              bg={isSelected ? "gray.300" : "transparent"}
            />
            <Text fontSize="sm" fontWeight="semibold">
              {plan.credits}
            </Text>
          </Box>

          <Flex align="baseline" gap={4} mb={4}>
            <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={whiteColor}>
              ${Number(plan.price).toLocaleString()}
            </Text>
            {plan.originalPrice && (
              <Text
                fontSize={{ base: "md", md: "lg" }}
                textDecoration="line-through"
                opacity={0.7}
              >
                ${Number(plan.originalPrice).toLocaleString()}
              </Text>
            )}
          </Flex>

          {plan.discountLabel && (
            <Text fontSize="sm" opacity={0.8} mb={3}>
              {plan.discountLabel}
            </Text>
          )}

          <Button
            w="full"
            colorScheme="brand"
            onClick={() => onBuy(plan)}
            isLoading={isBuying}
          >
            {plan.button}
          </Button>
        </Box>

        <Box>
          <Divider mb={3} />
          <Text fontSize="xs" color="gray.500">
            {plan.note}
          </Text>
        </Box>
      </Flex>

      {/* Hover gradient border (same pattern as your plan cards) */}
      <Box
        pos="absolute"
        inset={0}
        borderRadius="20px"
        zIndex={1}
        _before={{
          content: '""',
          pos: "absolute",
          top: "10px",
          left: "10px",
          right: "0px",
          bottom: "0px",
          borderRadius: "20px",
          bg: "linear-gradient(90deg, rgba(56,187,230,1) 0%, rgba(254,113,255,1) 46%, rgba(237,100,121,1) 63%, rgba(247,235,13,1) 99%)",
          opacity: 0,
          transition: "all 0.3s ease",
          zIndex: -1,
        }}
        _groupHover={{ _before: { opacity: 1, top: "8px", left: "8px" } }}
      />
    </Box>
  );
};

function BuyCredits() {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
  const boxBorderColor = useColorModeValue("gray.200", "purple.800");
  const textColor = useColorModeValue("#421e66", "#b5b5cd");

  const { data: plansData, isLoading: isPlansLoading } = useGetPlansQuery({
    planType: "credit",
  });
  const [buyCredits, { isLoading: isBuying }] = useBuyCreditsMutation();

  const plans = normalizePlans(plansData);

  // pick a default “popular” card to showcase the hover/offset look
  const popularId =
    plans.find((p) => p.isPopular)?.id || (plans[2]?.id ?? plans[0]?.id);

  const handleBuy = async (plan) => {
    try {
      await buyCredits({
        payload:{
            priceId: plan.priceId,
        }
      }).unwrap();
    } catch (e) {
      console.error(e);
    }
    buyCredits({  
        payload:{
            priceId: plan.priceId,
        }
    })
    .unwrap()
    .then(response => {
      window.open(response?.data?.url, '_blank');
    })
    .catch(error => {
        toast({
          position: "top-right",
          title: "Something went wrong",
          description: error?.data?.message || error?.message || "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
    });
  };

  return (
    <SidebarWithHeader>
      <Box>
        {/* Heading */}
        <Box spacing={2} textAlign="left" mb="20px" align="start">
          <Heading fontSize="3xl" color={textColor}>
            Purchase Credits
          </Heading>
          <Text fontSize="lg" color={textColor}>
            Select the number of credits you want to purchase
          </Text>
        </Box>
        
        {/* Balance */}
        <Flex
          bg={inputBackground}
          p={4}
          my={4}
          borderRadius="lg"
          border="1px solid"
          borderColor={boxBorderColor}
          direction="column"
          gap={2}
        >
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold">
            Available Credits
          </Text>
          <Flex align="center" justify="space-between">
            <Text fontSize="md" color={textColor}>
              $ 6,949
            </Text>
          </Flex>
        </Flex>

        {/* Cards */}
        <Box>
            {isPlansLoading ? (
                <Flex direction="column" gap={2} alignItems="center" justifyContent="center" h="50vh">
                    <Spinner />
                    <Text>Loading…</Text>
                </Flex>
            ) : (
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={8}>
                {(plans?.length ? plans : [1, 2, 3, 4]).map((p, idx) => (
                    <BuyingCard
                        key={p.id ?? idx}
                        plan={p}
                        colorMode={colorMode}
                        isSelected={p.id === popularId}
                        onBuy={handleBuy}
                        isBuying={isBuying}
                    />
                ))}
                </SimpleGrid>
            )}
        </Box>
      </Box>
    </SidebarWithHeader>
  );
}

export default BuyCredits;
